import { sendRequest } from "@infrastructure/api/api";
import { KpiResponse } from "@features/admin/kpi/types/kpi";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";
import { KpiUnitResponse } from "@features/admin/kpi/types/kpiUnits";
import { KpiDataTypeResponse } from "@features/admin/kpi/types/kpiDataTypes";
import { CreateKpiRequest, UpdateKpiRequest } from "../types/kpiRequests";

/** Convert booleans to 1/0 and strip null/undefined before URLSearchParams */
const toQueryParams = (params: Record<string, any>): Record<string, string> => {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        result[key] = typeof value === 'boolean' ? (value ? '1' : '0') : String(value);
    }
    return result;
};

export const fetchKpi = async (nationId: number, params: any): Promise<ApiResponse<KpiResponse>> => {
    const response = await sendRequest<any, KpiResponse>('GET', `nation/${nationId}/kpi/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};

export const fetchAssignedKpi = async (nationId: number, activityId: number, params: any): Promise<ApiResponse<KpiResponse>> => {
    const response = await sendRequest<any, KpiResponse>('GET', `nation/${nationId}/activity/${activityId}/kpi/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
};

export const fetchKpiUnits = async (nationId: number, params: any): Promise<ApiResponse<KpiUnitResponse>> => {
    const response = await sendRequest<any, KpiUnitResponse>('GET', `nation/${nationId}/kpi/unit/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
}

export const fetchKpiDataTypes = async (nationId: number, params: any): Promise<ApiResponse<KpiDataTypeResponse>> => {
    const response = await sendRequest<any, KpiDataTypeResponse>('GET', `nation/${nationId}/kpi/datatype/query?${new URLSearchParams(toQueryParams(params))}`);
    return response;
}

/**
 * Create a new KPI for a specific nation.
 *
 * Sends a POST request to the backend to create a KPI associated with the given nation ID.
 *
 * @param nationId - The numeric identifier of the nation the KPI will belong to.
 * @param data - The request payload containing KPI details (CreateKpiRequest).
 * @returns A Promise that resolves to an ApiResponse containing the created KpiResponse.
 *
 * @throws Propagates any error thrown by the underlying HTTP request (sendRequest), e.g. network or validation errors.
 */
export const createKpi = async (nationId: number, data: CreateKpiRequest): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<CreateKpiRequest, ApiMessageResponse>('POST', `nation/${nationId}/kpi/create`, data);
    return response;
};


/**
 * Update a KPI for a specific nation.
 *
 * Sends a POST request to `nation/{nationId}/kpi/{kpiId}/update` with the provided payload
 * and returns the API response containing the updated KPI.
 *
 * @param nationId - The numeric identifier of the nation that owns the KPI.
 * @param kpiId - The numeric identifier of the KPI to update.
 * @param data - The update payload conforming to UpdateKpiRequest.
 * @returns A promise that resolves to an ApiResponse containing the updated KpiResponse.
 *
 * @throws Will reject if the underlying HTTP request fails or the API returns an error.
 *
 */
export const updateKpi = async (nationId: number, kpiId: number, data: UpdateKpiRequest): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<UpdateKpiRequest, ApiMessageResponse>('POST', `nation/${nationId}/kpi/${kpiId}/update`, data);
    return response;
};

/** Disables a KPI (sets it inactive). */
export const disableKpi = async (
    nationId: number,
    kpiId: number
): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<Record<string, never>, ApiMessageResponse>(
        'POST',
        `nation/${nationId}/kpi/${kpiId}/disable`,
        {}
    );
};

/** Activities that currently have this KPI assigned (formula operand picker). */
export const fetchKpiActivities = async (
    nationId: number,
    kpiId: number
): Promise<ApiResponse<{ data: Array<{ activity_id: number; activity_name: string }> }>> => {
    return sendRequest<never, { data: Array<{ activity_id: number; activity_name: string }> }>(
        'GET',
        `nation/${nationId}/kpi/${kpiId}/activities`
    );
};

/** Enables a KPI (sets it active). */
export const enableKpi = async (nationId: number, kpiId: number): Promise<ApiResponse<ApiMessageResponse>> => {
    return sendRequest<never, ApiMessageResponse>('POST', `nation/${nationId}/kpi/${kpiId}/enable`);
};